import { gql } from '@apollo/client';

export const ALL_DRAFT_QUOTES_QUERY = gql`
  query AllDraftQuotes($domainId: Int!) {
    allDraftQuotes(condition: { domainId: $domainId }, orderBy: CREATED_AT_DESC) {
      nodes {
        id
        name
        createdAt
        finalizedQuoteId
        jobByJobId {
          id
          name
          customerByCustomerId {
            id
            name
          }
        }
      }
    }
  }
`;

export const CREATE_DRAFT_QUOTE_MUTATION = gql`
  mutation CreateDraftQuote(
    $domainId: Int!
    $jobId: Int!
    $name: String!
    $billToAddressId: Int
    $shipToAddressId: Int
    $notes: String
    $createdBy: Int!
  ) {
    createDraftQuote(
      input: {
        draftQuote: {
          domainId: $domainId
          jobId: $jobId
          name: $name
          billToAddressId: $billToAddressId
          shipToAddressId: $shipToAddressId
          notes: $notes
          createdBy: $createdBy
        }
      }
    ) {
      draftQuote {
        id
        name
      }
    }
  }
`;

export const UPDATE_DRAFT_QUOTE_MUTATION = gql`
  mutation UpdateDraftQuote($id: Int!, $billToAddressId: Int, $shipToAddressId: Int, $notes: String) {
    updateDraftQuoteById(
      input: {
        id: $id
        draftQuotePatch: { billToAddressId: $billToAddressId, shipToAddressId: $shipToAddressId, notes: $notes }
      }
    ) {
      draftQuote {
        id
      }
    }
  }
`;

export const DRAFT_QUOTE_BY_ID_QUERY = gql`
  query DraftQuoteById($id: Int!) {
    draftQuoteById(id: $id) {
      id
      name
      notes
      createdAt
      finalizedQuoteId
      jobByJobId {
        id
        name
        addressByAddressId {
          id
          street
          city
          state
          zip
        }
        customerByCustomerId {
          id
          name
          email
          phone
          addressesByCustomerId {
            nodes {
              id
              street
              city
              state
              zip
            }
          }
        }
      }
      addressByBillToAddressId {
        id
        street
        city
        state
        zip
      }
      addressByShipToAddressId {
        id
        street
        city
        state
        zip
      }
      draftQuoteObjectsByDraftQuoteId(orderBy: SORT_ORDER_ASC) {
        nodes {
          id
          name
          sortOrder
          quantity
          widthInches
          heightInches
          fabricDescription
          liningType
          bumpInterlining
          operatingSystem
          hardwareType
          itemType
          mountType
          rollDirection
          driveSide
          chainColor
          depthInches
          bedSize
          weltType
          insertType
          dustskirtStyle
          spaceAboveWindowInches
          trimPlacement
          fabricWidthInches
          liningWidthInches
          flapSizeInches
          patternRepeatInches
          liningDescription
          fasciaSize
          mechanism
          inventoryItemId
          interliningType
          fabricRepeatInches
          rodFaceInches
          overlapInches
          returnInches
          hemSize
          pleatType
          ruffle
          shamSize
          dropInches
          duvetInsert
          trimDescription
          archivedAt
          draftQuoteObjectItemsByDraftQuoteObjectId(orderBy: ID_ASC) {
            nodes {
              id
              section
              productType
              description
              quantity
              unitPrice
              notes
              formulaNote
            }
          }
          draftQuoteObjectImagesByDraftQuoteObjectId(orderBy: CREATED_AT_ASC) {
            nodes {
              id
              storedFilename
              originalFilename
              createdAt
              archivedAt
            }
          }
        }
      }
    }
  }
`;

// Calls the custom addDraftQuoteObject plugin which creates the object
// and auto-generates section-tagged items from SOP calculations.
export const ADD_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $itemType: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $liningType: String
    $bumpInterlining: Boolean
    $operatingSystem: String
    $hardwareType: String
    $mountType: String
    $rollDirection: String
    $driveSide: String
    $chainColor: String
    $bedSize: String
    $weltType: String
    $insertType: String
    $dustskirtStyle: String
    $spaceAboveWindowInches: Float
    $trimPlacement: String
    $fabricWidthInches: Float
    $liningWidthInches: Float
    $flapSizeInches: Float
    $patternRepeatInches: Float
    $liningDescription: String
    $fasciaSize: Int
    $mechanism: String
    $inventoryItemId: Int
    $interliningType: String
    $fabricRepeatInches: Float
    $rodFaceInches: Float
    $overlapInches: Float
    $returnInches: Float
    $hemSize: String
    $pleatType: String
    $ruffle: Boolean
    $shamSize: String
    $dropInches: Float
    $trimDescription: String
  ) {
    addDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      itemType: $itemType
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      bumpInterlining: $bumpInterlining
      operatingSystem: $operatingSystem
      hardwareType: $hardwareType
      mountType: $mountType
      rollDirection: $rollDirection
      driveSide: $driveSide
      chainColor: $chainColor
      bedSize: $bedSize
      weltType: $weltType
      insertType: $insertType
      dustskirtStyle: $dustskirtStyle
      spaceAboveWindowInches: $spaceAboveWindowInches
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      liningWidthInches: $liningWidthInches
      flapSizeInches: $flapSizeInches
      patternRepeatInches: $patternRepeatInches
      liningDescription: $liningDescription
      fasciaSize: $fasciaSize
      mechanism: $mechanism
      inventoryItemId: $inventoryItemId
      interliningType: $interliningType
      fabricRepeatInches: $fabricRepeatInches
      rodFaceInches: $rodFaceInches
      overlapInches: $overlapInches
      returnInches: $returnInches
      hemSize: $hemSize
      pleatType: $pleatType
      ruffle: $ruffle
      shamSize: $shamSize
      dropInches: $dropInches
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

// Calls the custom updateDraftQuoteObject plugin — the "go back and edit"
// flow: updates an existing object's own columns and replaces its
// SOP-calculated items (delete + recompute), mirroring addDraftQuoteObject's
// resolution but scoped to one already-saved object.
export const UPDATE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $itemType: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $liningType: String
    $bumpInterlining: Boolean
    $operatingSystem: String
    $hardwareType: String
    $mountType: String
    $rollDirection: String
    $driveSide: String
    $chainColor: String
    $bedSize: String
    $weltType: String
    $insertType: String
    $dustskirtStyle: String
    $spaceAboveWindowInches: Float
    $trimPlacement: String
    $fabricWidthInches: Float
    $liningWidthInches: Float
    $flapSizeInches: Float
    $patternRepeatInches: Float
    $liningDescription: String
    $fasciaSize: Int
    $mechanism: String
    $inventoryItemId: Int
    $interliningType: String
    $fabricRepeatInches: Float
    $rodFaceInches: Float
    $overlapInches: Float
    $returnInches: Float
    $hemSize: String
    $pleatType: String
    $ruffle: Boolean
    $shamSize: String
    $dropInches: Float
    $trimDescription: String
  ) {
    updateDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      itemType: $itemType
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      bumpInterlining: $bumpInterlining
      operatingSystem: $operatingSystem
      hardwareType: $hardwareType
      mountType: $mountType
      rollDirection: $rollDirection
      driveSide: $driveSide
      chainColor: $chainColor
      bedSize: $bedSize
      weltType: $weltType
      insertType: $insertType
      dustskirtStyle: $dustskirtStyle
      spaceAboveWindowInches: $spaceAboveWindowInches
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      liningWidthInches: $liningWidthInches
      flapSizeInches: $flapSizeInches
      patternRepeatInches: $patternRepeatInches
      liningDescription: $liningDescription
      fasciaSize: $fasciaSize
      mechanism: $mechanism
      inventoryItemId: $inventoryItemId
      interliningType: $interliningType
      fabricRepeatInches: $fabricRepeatInches
      rodFaceInches: $rodFaceInches
      overlapInches: $overlapInches
      returnInches: $returnInches
      hemSize: $hemSize
      pleatType: $pleatType
      ruffle: $ruffle
      shamSize: $shamSize
      dropInches: $dropInches
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

// Shirred Valance gets its own dedicated add/update mutations instead of the
// generic ADD_DRAFT_QUOTE_OBJECT_MUTATION/UPDATE_DRAFT_QUOTE_OBJECT_MUTATION
// above — a deliberate deviation from every other item type, requested so
// this type's create/update path has its own narrow, typed GraphQL surface.
// See createShirredValanceDraftQuoteObjectPlugin.ts / updateShirredValanceDraftQuoteObjectPlugin.ts.
export const ADD_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddShirredValanceDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricDescription: String
    $liningType: String
    $interliningType: String
    $trimPlacement: String
    $fabricWidthInches: Float
    $fabricRepeatInches: Float
    $inventoryItemId: Int
    $liningDescription: String
    $trimDescription: String
  ) {
    addShirredValanceDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      interliningType: $interliningType
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      fabricRepeatInches: $fabricRepeatInches
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateShirredValanceDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricDescription: String
    $liningType: String
    $interliningType: String
    $trimPlacement: String
    $fabricWidthInches: Float
    $fabricRepeatInches: Float
    $inventoryItemId: Int
    $liningDescription: String
    $trimDescription: String
  ) {
    updateShirredValanceDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      interliningType: $interliningType
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      fabricRepeatInches: $fabricRepeatInches
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const ADD_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBoxSpringCoverDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    addBoxSpringCoverDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBoxSpringCoverDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    updateBoxSpringCoverDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const ADD_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddAustrianShadeDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
  ) {
    addAustrianShadeDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateAustrianShadeDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
  ) {
    updateAustrianShadeDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const ADD_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBalloonShadeDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
  ) {
    addBalloonShadeDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBalloonShadeDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
  ) {
    updateBalloonShadeDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
    ) {
      objectId
    }
  }
`;

export const ADD_DUVET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddDuvetDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $weltType: String
    $duvetInsert: Boolean
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    addDuvetDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      weltType: $weltType
      duvetInsert: $duvetInsert
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_DUVET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateDuvetDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $weltType: String
    $duvetInsert: Boolean
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    updateDuvetDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      weltType: $weltType
      duvetInsert: $duvetInsert
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const ADD_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddCoverletDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    addCoverletDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateCoverletDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    updateCoverletDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const ADD_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddTableclothRoundDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    addTableclothRoundDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateTableclothRoundDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    updateTableclothRoundDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const ADD_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddTableclothRectangleDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    addTableclothRectangleDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

export const UPDATE_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateTableclothRectangleDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
  ) {
    updateTableclothRectangleDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
    ) {
      objectId
    }
  }
`;

// For manually adding a custom line item (fabric swatch, special hardware, etc.)
// to an existing object. Uses PostGraphile's auto-generated mutation.
export const CREATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION = gql`
  mutation CreateDraftQuoteObjectItem(
    $draftQuoteId: Int!
    $draftQuoteObjectId: Int!
    $section: String!
    $description: String!
    $quantity: BigFloat!
    $unitPrice: BigInt!
    $notes: String
  ) {
    createDraftQuoteObjectItem(
      input: {
        draftQuoteObjectItem: {
          draftQuoteId: $draftQuoteId
          draftQuoteObjectId: $draftQuoteObjectId
          section: $section
          description: $description
          quantity: $quantity
          unitPrice: $unitPrice
          notes: $notes
        }
      }
    ) {
      draftQuoteObjectItem {
        id
      }
    }
  }
`;

export const UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION = gql`
  mutation UpdateDraftQuoteObjectItem($id: Int!, $quantity: BigFloat!, $description: String!, $unitPrice: BigInt!) {
    updateDraftQuoteObjectItemById(input: {
      id: $id
      draftQuoteObjectItemPatch: { quantity: $quantity, description: $description, unitPrice: $unitPrice }
    }) {
      draftQuoteObjectItem { id }
    }
  }
`;

// Issue #51: lets the shop correct a line item's QuickBooks product_type by
// hand (e.g. a mis-tagged legacy row) without touching its price/qty/desc —
// a separate mutation from UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION above so
// existing quantity/description/price edit call sites are untouched.
export const UPDATE_DRAFT_QUOTE_OBJECT_ITEM_PRODUCT_TYPE_MUTATION = gql`
  mutation UpdateDraftQuoteObjectItemProductType($id: Int!, $productType: ProductTypeEnum) {
    updateDraftQuoteObjectItemById(input: {
      id: $id
      draftQuoteObjectItemPatch: { productType: $productType }
    }) {
      draftQuoteObjectItem { id }
    }
  }
`;

// $archivedAt is nullable so this mutation can also un-archive (pass null) —
// used by the "Go back" edit flow to restore an object if its edit is
// abandoned instead of saved.
export const ARCHIVE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation ArchiveDraftQuoteObject($id: Int!, $archivedAt: Datetime) {
    updateDraftQuoteObjectById(input: {
      id: $id
      draftQuoteObjectPatch: { archivedAt: $archivedAt }
    }) {
      draftQuoteObject { id }
    }
  }
`;

export const DELETE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation DeleteDraftQuoteObject($id: Int!) {
    deleteDraftQuoteObjectById(input: { id: $id }) {
      deletedDraftQuoteObjectId
    }
  }
`;

export const ARCHIVE_DRAFT_QUOTE_OBJECT_IMAGE_MUTATION = gql`
  mutation ArchiveDraftQuoteObjectImage($id: Int!, $archivedAt: Datetime!) {
    updateDraftQuoteObjectImageById(input: {
      id: $id
      draftQuoteObjectImagePatch: { archivedAt: $archivedAt }
    }) {
      draftQuoteObjectImage { id }
    }
  }
`;

export const FINALIZE_QUOTE_MUTATION = gql`
  mutation FinalizeQuote($draftQuoteId: Int!) {
    finalizeQuote(draftQuoteId: $draftQuoteId) {
      quoteId
    }
  }
`;
